import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Chess } from 'chess.js';
import type { Line, LineMove } from '../../../shared/types';
import type { AttemptedMove } from '../board/Chessboard';

/**
 * Plays one repertoire line as a practice.
 *
 * The trainee plays their own color; the other side's replies are played
 * automatically after a short pause so the move is visible rather than
 * instantaneous. A correct move advances the line and reveals the reason it is
 * played; a wrong move is refused, counted, and answered with the explanation
 * of what should have been played instead.
 *
 * Correctness is checked by *coordinates*, not by comparing move text: the
 * expected SAN is replayed on a scratch board to find its from/to squares, and
 * those are compared with what the user played. That makes the check immune to
 * notation differences between the Python build script that authored the line
 * and the JavaScript engine that renders it — and it yields the squares to
 * highlight when showing the answer.
 */

export type TrainerMode = 'study' | 'practice';

export type Feedback =
  | { kind: 'idle' }
  /** Study mode: here is the move to play, and why, before you play it. */
  | { kind: 'teaching'; move: LineMove; san: string }
  | { kind: 'correct'; move: LineMove; san: string }
  | { kind: 'wrong'; expected: LineMove; expectedSan: string; played: string }
  | { kind: 'revealed'; expected: LineMove; expectedSan: string };

export interface TrainerState {
  mode: TrainerMode;
  fen: string;
  /** How many moves of the line have been played. */
  ply: number;
  totalPlies: number;
  finished: boolean;
  /** True while the opponent's reply is pending, so the board is locked. */
  waitingForOpponent: boolean;
  isTraineeTurn: boolean;
  feedback: Feedback;
  lastMove: { from: string; to: string } | null;
  /** Set after a mistake or a reveal, to show the right move on the board. */
  hintMove: { from: string; to: string } | null;
  /** Trainee moves played correctly at the first attempt. */
  movesCorrect: number;
  /** Trainee moves reached so far — the denominator for accuracy. */
  movesAttempted: number;
  /** Total trainee moves in the whole line. */
  movesTotal: number;
  /** The moves played so far, for the move list. */
  history: { san: string; move: LineMove }[];
}

export interface Trainer extends TrainerState {
  /** Offer a move from the board. Silently ignored when it is not your turn. */
  play: (attempt: AttemptedMove) => void;
  /** Study mode: play the move being taught, without having to find it. */
  advance: () => void;
  /** Step the board back one move. Study mode only. */
  back: () => void;
  /** Jump to any point in the line. Study mode only. */
  seekTo: (ply: number) => void;
  /**
   * Whether seeking is allowed. False in Practice, where jumping around would
   * skip past the moves being tested -- which is the entire exercise.
   */
  canSeek: boolean;
  /** Give up on the current move and show the answer. Counts as a mistake. */
  reveal: () => void;
  /** Start the line again from move one. */
  restart: () => void;
}

/** Replays `sans` from the start and returns the resulting position. */
function positionAfter(sans: string[]): Chess {
  const chess = new Chess();
  for (const san of sans) chess.move(san);
  return chess;
}

export function useLineTrainer(
  line: Line | null,
  opponentDelayMs = 350,
  mode: TrainerMode = 'practice',
): Trainer {
  // White moves on even plies, Black on odd ones.
  const traineeParity = line?.side === 'black' ? 1 : 0;
  const movesTotal = useMemo(
    () => (line ? line.moves.filter((_, i) => i % 2 === traineeParity).length : 0),
    [line, traineeParity],
  );

  const [ply, setPly] = useState(0);
  const [feedback, setFeedback] = useState<Feedback>({ kind: 'idle' });
  const [lastMove, setLastMove] = useState<{ from: string; to: string } | null>(null);
  const [hintMove, setHintMove] = useState<{ from: string; to: string } | null>(null);
  const [movesCorrect, setMovesCorrect] = useState(0);
  const [waitingForOpponent, setWaitingForOpponent] = useState(false);
  // Moves the trainee has already got wrong once, so a later success on the
  // same move does not quietly count as first-time-correct.
  const failedPlies = useRef<Set<number>>(new Set());

  // Restart whenever the line changes, *during render* rather than in an effect.
  //
  // An effect runs after the render commits, which leaves exactly one frame in
  // which `line` is the new line while `ply` and `feedback` still describe the
  // old one. That frame reaches the children: the coach renders the previous
  // line's teaching feedback -- including its playable mistakes -- against the
  // new line's position, and MistakeExplorer replays a Scandinavian mistake on
  // a Queen's Gambit board. chess.js throws and the page white-screens.
  //
  // This is React's documented pattern for adjusting state when a prop changes:
  // set it during render, guarded by the value it is tracking, so React
  // re-renders with the corrected state before anything is committed and no
  // child ever observes the mismatch.
  const [renderedFor, setRenderedFor] = useState<string>(`${line?.id ?? ''}:${mode}`);
  const key = `${line?.id ?? ''}:${mode}`;
  if (key !== renderedFor) {
    setRenderedFor(key);
    setPly(0);
    setFeedback({ kind: 'idle' });
    setLastMove(null);
    setHintMove(null);
    setMovesCorrect(0);
    setWaitingForOpponent(false);
    failedPlies.current = new Set();
  }

  const sansSoFar = useMemo(
    () => (line ? line.moves.slice(0, ply).map((m) => m.san) : []),
    [line, ply],
  );

  const position = useMemo(() => positionAfter(sansSoFar), [sansSoFar]);
  const fen = position.fen();

  const finished = !!line && ply >= line.moves.length;
  const isTraineeTurn = !!line && !finished && ply % 2 === traineeParity;

  /** The squares of the move the line expects next, or null at the end. */
  const expectedSquares = useMemo(() => {
    if (!line || finished) return null;
    const scratch = positionAfter(sansSoFar);
    try {
      const move = scratch.move(line.moves[ply].san);
      return { from: move.from, to: move.to, promotion: move.promotion, san: move.san };
    } catch {
      // A line that fails to replay is a content bug; the build script should
      // have caught it. Fail visibly rather than locking the trainer up.
      console.error(`Line ${line.id} has an unplayable move at ply ${ply}`);
      return null;
    }
  }, [line, sansSoFar, ply, finished]);

  // Auto-play the opponent's reply after a short pause.
  useEffect(() => {
    if (!line || finished || isTraineeTurn) {
      setWaitingForOpponent(false);
      return;
    }
    setWaitingForOpponent(true);
    const id = window.setTimeout(() => {
      const scratch = positionAfter(line.moves.slice(0, ply).map((m) => m.san));
      try {
        const move = scratch.move(line.moves[ply].san);
        setLastMove({ from: move.from, to: move.to });
      } catch {
        console.error(`Line ${line.id} has an unplayable move at ply ${ply}`);
      }
      setHintMove(null);
      setPly((p) => p + 1);
      setWaitingForOpponent(false);
    }, Math.max(0, opponentDelayMs));

    return () => window.clearTimeout(id);
  }, [line, ply, isTraineeTurn, finished, opponentDelayMs]);

  // Study mode shows the answer up front: the move to play is highlighted and
  // its reasoning is on screen before you touch a piece.
  useEffect(() => {
    if (mode !== 'study') return;
    if (!line || finished || !isTraineeTurn || !expectedSquares) return;
    setFeedback({ kind: 'teaching', move: line.moves[ply], san: expectedSquares.san });
    setHintMove({ from: expectedSquares.from, to: expectedSquares.to });
  }, [mode, line, finished, isTraineeTurn, expectedSquares, ply]);

  const play = useCallback(
    (attempt: AttemptedMove) => {
      if (!line || !isTraineeTurn || !expectedSquares) return;

      const matches =
        attempt.from === expectedSquares.from &&
        attempt.to === expectedSquares.to &&
        // A promotion only has to match when the expected move is one.
        (!expectedSquares.promotion || attempt.promotion === expectedSquares.promotion);

      const expectedMove = line.moves[ply];

      if (!matches) {
        // Name what was actually played, so the feedback can be specific.
        const scratch = positionAfter(sansSoFar);
        let playedSan = `${attempt.from}-${attempt.to}`;
        try {
          playedSan = scratch.move({
            from: attempt.from,
            to: attempt.to,
            promotion: attempt.promotion,
          }).san;
        } catch {
          /* keep the coordinate form */
        }

        // Only practicing counts a wrong move against you. In study mode the
        // answer was already on screen, so getting it wrong means "I clicked
        // the wrong square", not "I did not know this".
        if (mode === 'practice') failedPlies.current.add(ply);
        setFeedback({
          kind: 'wrong',
          expected: expectedMove,
          expectedSan: expectedSquares.san,
          played: playedSan,
        });
        setHintMove({ from: expectedSquares.from, to: expectedSquares.to });
        return;
      }

      if (!failedPlies.current.has(ply)) setMovesCorrect((n) => n + 1);
      setFeedback({ kind: 'correct', move: expectedMove, san: expectedSquares.san });
      setLastMove({ from: expectedSquares.from, to: expectedSquares.to });
      setHintMove(null);
      setPly((p) => p + 1);
    },
    [line, isTraineeTurn, expectedSquares, ply, sansSoFar],
  );

  /**
   * Play the move the line calls for, without the user having to find it.
   * Study mode's "Next" button — the point there is to read the reasoning and
   * see the position develop, not to be tested on recall.
   */
  const advance = useCallback(() => {
    if (!line || !isTraineeTurn || !expectedSquares) return;
    setFeedback({ kind: 'correct', move: line.moves[ply], san: expectedSquares.san });
    setLastMove({ from: expectedSquares.from, to: expectedSquares.to });
    setHintMove(null);
    setPly((p) => p + 1);
  }, [line, isTraineeTurn, expectedSquares, ply]);

  const reveal = useCallback(() => {
    if (!line || !isTraineeTurn || !expectedSquares) return;
    failedPlies.current.add(ply);
    setFeedback({
      kind: 'revealed',
      expected: line.moves[ply],
      expectedSan: expectedSquares.san,
    });
    setHintMove({ from: expectedSquares.from, to: expectedSquares.to });
  }, [line, isTraineeTurn, expectedSquares, ply]);

  /**
   * Move the board to an arbitrary point in the line.
   *
   * Study mode only, and deliberately so: seeking in Practice would let you
   * skip past the moves you are being tested on, which is the whole exercise.
   * Re-reading the reasoning for a move you have already passed is the point in
   * Study, and being unable to go back was an odd gap.
   */
  const seekTo = useCallback(
    (target: number) => {
      if (!line || mode !== 'study') return;
      const next = Math.max(0, Math.min(line.moves.length, target));
      setPly(next);
      setHintMove(null);
      if (next === 0) {
        setFeedback({ kind: 'idle' });
        setLastMove(null);
        return;
      }
      // Show the move that was just played, and highlight the squares it used,
      // so stepping backwards reads the same as stepping forwards.
      const scratch = positionAfter(line.moves.slice(0, next - 1).map((m) => m.san));
      try {
        const move = scratch.move(line.moves[next - 1].san);
        setLastMove({ from: move.from, to: move.to });
        setFeedback({ kind: 'correct', move: line.moves[next - 1], san: move.san });
      } catch {
        setLastMove(null);
        setFeedback({ kind: 'idle' });
      }
    },
    [line, mode],
  );

  const back = useCallback(() => seekTo(ply - 1), [seekTo, ply]);

  const restart = useCallback(() => {
    setPly(0);
    setFeedback({ kind: 'idle' });
    setLastMove(null);
    setHintMove(null);
    setMovesCorrect(0);
    failedPlies.current = new Set();
  }, []);

  const history = useMemo(() => {
    if (!line) return [];
    const chess = new Chess();
    const out: { san: string; move: LineMove }[] = [];
    for (const move of line.moves.slice(0, ply)) {
      const played = chess.move(move.san);
      out.push({ san: played.san, move });
    }
    return out;
  }, [line, ply]);

  const movesAttempted = useMemo(
    () => (line ? line.moves.slice(0, ply).filter((_, i) => i % 2 === traineeParity).length : 0),
    [line, ply, traineeParity],
  );

  return {
    mode,
    fen,
    ply,
    totalPlies: line?.moves.length ?? 0,
    finished,
    waitingForOpponent,
    isTraineeTurn,
    feedback,
    lastMove,
    hintMove,
    movesCorrect,
    movesAttempted,
    movesTotal,
    history,
    play,
    advance,
    back,
    seekTo,
    /** Seeking is a Study-mode affordance; Practice must not skip the test. */
    canSeek: mode === 'study',
    reveal,
    restart,
  };
}
